import type {
  ZReportPayload,
  ZHourBucket,
  ZVoidEntry, ZVoidsTotal,
  ZRefundEntry,
  ZCategoryRow,
  ZItemRow,
  ZStaffServiceChargeRow,
  ShiftClosing,
} from '@/types'
import {
  dispatchCustomerReceiptPrint,
  type CustomerReceiptSettings,
} from '@/lib/printCustomerReceipt'
import { thermalPrintBaseCss } from '@/lib/thermalPrintCss'
import { escapeHtml } from '@/lib/printReport'
import { formatDateTimeHuman, formatDateHuman, formatTimeHuman } from '@/lib/utils'

/**
 * Z report — the printable end-of-day slip. Designed for 80mm thermal
 * printers in monochrome (black). Renders through the same off-screen
 * iframe used by customer receipts (no popup window).
 *
 * Sections, top-to-bottom: header, lifecycle, P&L ladder, tender, cash
 * reconciliation, hourly sparkline, voids, refunds, discounts, service
 * charges by staff, top categories & items, manager sign-off, footer.
 */
/** F-MM-N30: which house-account lines the slip may print (the tenant's switch). */
export interface ZHouseAccountsGate {
  /** Tabs on, or tabs off while money is still owed. */
  tabsVisible: boolean
  staff: boolean
}

const NO_HOUSE_ACCOUNTS: ZHouseAccountsGate = { tabsVisible: false, staff: false }

export function printZReport(
  payload: ZReportPayload,
  cfg: CustomerReceiptSettings,
  formatCurrency: (n: number) => string,
  houseAccounts: ZHouseAccountsGate = NO_HOUSE_ACCOUNTS,
): { printed: boolean } {
  const html = buildZReportHtml(payload, cfg, formatCurrency, houseAccounts)
  return { printed: dispatchCustomerReceiptPrint(html) }
}

/**
 * Net profit for the daily view: gross profit − operating expenses. The
 * staff-meal food cost is a monthly P&L line, so it is added back here.
 */
export function zDailyNetProfit(s: Pick<ZReportPayload['summary'], 'net_profit' | 'staff_meals_cost'>): number {
  return s.net_profit + (s.staff_meals_cost ?? 0)
}

export interface ZDrawerLine {
  label: string
  amount: number
  sign: '' | '+' | '−'
}

/**
 * The cash drawer ladder. Opening float + cash sales + tab payments in cash +
 * paid in − paid out = expected in drawer (the sealed expected_cash already
 * counts tab cash, so the slip must show it to add up).
 */
export function zCashDrawerLines(z: ZReportPayload, gate: ZHouseAccountsGate): ZDrawerLine[] {
  const paidIn = (z.cash_movements || [])
    .filter((m) => m.movement_type === 'paid_in')
    .reduce((acc, m) => acc + m.amount, 0)
  const paidOut = (z.cash_movements || [])
    .filter((m) => m.movement_type === 'paid_out')
    .reduce((acc, m) => acc + m.amount, 0)
  // With tab lines hidden, any tab cash still reaches the drawer, so it rides
  // inside Cash sales (as Day Close does) and the ladder still adds up.
  const tabCash = z.summary.tab_settlements?.cash ?? 0
  const showTab = gate.tabsVisible && tabCash > 0
  return [
    { label: 'Opening float', amount: z.day.opening_cash ?? 0, sign: '' },
    { label: '+ Cash sales', amount: z.summary.payments.cash + (showTab ? 0 : tabCash), sign: '+' },
    ...(showTab ? [{ label: '+ Tab payments (cash)', amount: tabCash, sign: '+' as const }] : []),
    { label: '+ Paid in', amount: paidIn, sign: '+' },
    { label: '− Paid out', amount: paidOut, sign: '−' },
  ]
}

function buildZReportHtml(
  z: ZReportPayload,
  cfg: CustomerReceiptSettings,
  fc: (n: number) => string,
  gate: ZHouseAccountsGate,
): string {
  const seqLabel = `Z-${String(z.sequence).padStart(4, '0')}`
  const closeTime =
    z.day.closed_at ? formatDateTimeHuman(z.day.closed_at) : formatDateTimeHuman(z.generated_at)
  const reprintBanner = isReprintBanner(z) ? '<div class="reprint">REPRINT</div>' : ''

  const hdr = `
    <div class="hdr">
      ${cfg.logoUrl ? `<img class="logo" src="${escapeHtml(cfg.logoUrl)}" alt="" />` : ''}
      <div class="biz">${escapeHtml(cfg.businessName || 'Restaurant')}</div>
      ${cfg.address ? `<div class="meta">${escapeHtml(cfg.address)}</div>` : ''}
      ${cfg.phone ? `<div class="meta">${escapeHtml(cfg.phone)}</div>` : ''}
      ${cfg.ntn ? `<div class="meta">NTN: ${escapeHtml(cfg.ntn)}</div>` : ''}
      ${cfg.posNumber ? `<div class="meta">POS #: ${escapeHtml(cfg.posNumber)}</div>` : ''}
    </div>

    <div class="title">Z REPORT &mdash; END OF DAY</div>
    <div class="zhead">
      <div><span class="lbl">Z #</span><span class="val">${escapeHtml(seqLabel)}</span></div>
      <div><span class="lbl">Business date</span><span class="val">${escapeHtml(
        formatDateHuman(z.day.business_date),
      )}</span></div>
      <div><span class="lbl">Closed</span><span class="val">${escapeHtml(closeTime)}</span></div>
      <div><span class="lbl">Status</span><span class="val">${escapeHtml(z.day.status.toUpperCase())}</span></div>
    </div>
    ${reprintBanner}
  `

  const lifecycle = `
    <div class="section">
      <div class="section-title">Lifecycle</div>
      <table class="kv">
        ${kvRow('Opened by', `${nameOrDash(z.day.opened_by_name)}${z.day.opened_at ? ` · ${escapeHtml(formatTimeHuman(z.day.opened_at))}` : ''}`)}
        ${kvRow('Staged by', `${nameOrDash(z.day.staged_by_name)}${z.day.staged_at ? ` · ${escapeHtml(formatTimeHuman(z.day.staged_at))}` : ''}`)}
        ${kvRow('Closed by', `${nameOrDash(z.day.closed_by_name)}${z.day.closed_at ? ` · ${escapeHtml(formatTimeHuman(z.day.closed_at))}` : ''}`)}
      </table>
    </div>
  `

  const s = z.summary
  const totals = `
    <div class="section">
      <div class="section-title">Sales totals</div>
      <table class="kv">
        ${kvRow('Orders', String(s.order_count))}
        ${s.guest_count > 0 ? kvRow('Covers', String(s.guest_count)) : ''}
        ${s.order_count > 0 ? kvRow('Average ticket', escapeHtml(fc(s.avg_ticket))) : ''}
      </table>
      <div class="rule"></div>
      <table class="kv">
        ${kvRow('Gross sales', escapeHtml(fc(s.gross_sales)))}
        ${
          // Restaurant vs Foodpanda split, only on a marketplace day; the two
          // lines add up to Gross sales. Matches the Day Close tiles.
          (s.marketplace_sales ?? 0) > 0
            ? kvRow('  Restaurant sales', escapeHtml(fc(Math.max(0, s.gross_sales - (s.marketplace_sales ?? 0)))))
            : ''
        }
        ${
          (s.marketplace_sales ?? 0) > 0
            ? kvRow('  Foodpanda sales (paid to you later)', escapeHtml(fc(s.marketplace_sales ?? 0)))
            : ''
        }
        ${kvRow('− Sales tax', escapeHtml(fc(s.sales_tax)))}
        ${
          // F-MM-N9: shown only when the tenant charges it, so the slip is
          // unchanged for opted-out and PRA tenants. Sits beside sales tax
          // because it is the same kind of number — collected and remitted,
          // never earned.
          (s.pos_fees_collected ?? 0) > 0
            ? kvRow('− FBR POS fees', escapeHtml(fc(s.pos_fees_collected ?? 0)))
            : ''
        }
        ${kvRow('Net sales', escapeHtml(fc(s.net_sales)), 'sum')}
        ${kvRow('− COGS', escapeHtml(fc(s.cogs)))}
        ${kvRow('Gross profit', escapeHtml(fc(s.gross_profit)), 'sum')}
        ${kvRow('− Operating expenses', escapeHtml(fc(s.operating_expenses)))}
        ${kvRow('Net profit', escapeHtml(fc(zDailyNetProfit(s))), 'sum-strong')}
        ${
          // F-MM-N23: memo lines only when a marketplace type was used today.
          // The commission is an ESTIMATE from the order type's % — the
          // platform's statement is what gets booked, never this figure.
          (s.marketplace_commission_est ?? 0) > 0
            ? kvRow('Marketplace commission (est.)', escapeHtml(fc(s.marketplace_commission_est ?? 0)))
            : ''
        }
        ${
          (s.marketplace_commission_est ?? 0) > 0 && s.net_profit_after_platform_fees_est != null
            ? kvRow(
                'Net profit after est. platform fees',
                escapeHtml(fc(s.net_profit_after_platform_fees_est + (s.staff_meals_cost ?? 0))),
                'sum',
              )
            : ''
        }
      </table>
      <div class="rule"></div>
      <table class="kv">
        ${kvRow('Discounts given', escapeHtml(fc(s.discounts)))}
        ${
          // F-RPT-N17: collected for staff, not earned — kept out of the
          // ladder table above so it never reads as a P&L deduction. Only
          // printed when the tenant actually charges it. Excludes fully
          // credit-noted orders, so it matches the by-staff section below.
          (s.service_charge ?? 0) > 0
            ? kvRow('Service charge', escapeHtml(fc(s.service_charge ?? 0)))
            : ''
        }
        ${kvRow('Refunds issued', escapeHtml(fc(s.refunds_issued)))}
        ${kvRow('Gross after refunds', escapeHtml(fc(s.gross_sales_after_refunds)))}
      </table>
      ${
        // F-MM-N30: memo only — staff meals are never sales or profit here.
        gate.staff && (s.staff_meal_orders ?? 0) > 0
          ? `<div class="meta">Staff meals: ${s.staff_meal_orders} meal${s.staff_meal_orders === 1 ? '' : 's'} · ${escapeHtml(
              fc(s.staff_meal_value ?? 0),
            )} menu value · ${escapeHtml(fc(s.staff_meals_cost ?? 0))} cost (on the monthly P&amp;L)</div>`
          : ''
      }
    </div>
  `

  // Online provider sub-breakdown (migration 050) prints indented beneath the
  // Online line. Providers aren't counted separately, so only the System column
  // is filled — the amounts reconcile to the Online system total.
  const onlineSub = z.summary.online_submethods ?? []
  // Card/online use the SEALED expected_* when the day was closed through the
  // wizard: that figure is net sales for the day PLUS cross-day tender swiped
  // today against earlier orders, which is what actually lands on tonight's
  // machine / bank batch. Using raw sales here made the slip's Δ disagree with
  // the Δ the manager was shown at close whenever cross-day tender existed.
  // Falls back to sales for older rows that predate the sealed columns.
  // Cash stays sales-based — the Cash drawer section below owns the float math.
  // F-MM-N23: the platform-settled slice is a receivable — it never lands on
  // tonight's batch, so the fallback expected-online figure excludes it (the
  // sealed expected_online already does).
  const platformSettled = z.summary.platform_settled_online ?? 0
  // F-MM-N30: tab payments land in tonight's card batch / online account, so
  // the sealed expected_* include them; the fallback adds them the same way.
  const tabCard = z.summary.tab_settlements?.card ?? 0
  const tabOnline = z.summary.tab_settlements?.online ?? 0
  const cardSystem = z.day.expected_card ?? z.summary.payments.card + tabCard
  const onlineSystem =
    z.day.expected_online ?? Math.max(0, z.summary.payments.online - platformSettled) + tabOnline
  const onTab = z.summary.payments.house_account ?? 0
  // F-CTRL-N67: the marketplace slice gets its own row — POS figure vs the
  // partner-app total the manager entered — instead of a receivable footnote
  // under Online that nobody could act on.
  const marketplaceSystem = z.day.expected_marketplace ?? platformSettled
  const showMarketplace = platformSettled > 0 || z.day.counted_marketplace != null
  const tenderRows = [
    tenderRow('Cash', z.summary.payments.cash, z.day.counted_cash, fc),
    tenderRow('Card', cardSystem, z.day.counted_card, fc),
    tenderRow('Online', onlineSystem, z.day.counted_online, fc),
    ...onlineSub.map((sm) => subTenderRow(sm.label, sm.amount, fc)),
    showMarketplace
      ? tenderRow('Foodpanda app', marketplaceSystem, z.day.counted_marketplace, fc)
      : '',
    onTab > 0 ? uncountedTenderRow('On tab (paid later)', onTab, fc) : '',
  ].join('')
  const tabNotes = gate.tabsVisible
    ? [
        tabCard > 0 ? `<div class="meta">Card includes ${escapeHtml(fc(tabCard))} of tab payments.</div>` : '',
        tabOnline > 0 ? `<div class="meta">Online includes ${escapeHtml(fc(tabOnline))} of tab payments.</div>` : '',
      ].join('')
    : ''
  const platformSettledNote =
    showMarketplace
      ? `<div class="meta">Foodpanda: POS figure vs the partner app. Not in cash, card or online — paid to you later, after commission (${escapeHtml(fc(platformSettled))} today).</div>`
      : ''
  const tender = `
    <div class="section">
      <div class="section-title">Tender breakdown</div>
      <table class="tender">
        <thead>
          <tr><th>Method</th><th class="num">System</th><th class="num">Counted</th><th class="num">Δ</th></tr>
        </thead>
        <tbody>${tenderRows}</tbody>
      </table>
      ${tabNotes}
      ${platformSettledNote}
    </div>
  `

  const drawerLines = zCashDrawerLines(z, gate)
  const expected = z.day.expected_cash ?? 0
  const counted = z.day.counted_cash ?? 0
  const variance = z.day.cash_variance ?? counted - expected
  const movementsList =
    z.cash_movements && z.cash_movements.length > 0
      ? `
    <div class="movements">
      ${z.cash_movements
        .map(
          (m) => `
        <div class="mv">
          <span class="mv-sign ${m.movement_type === 'paid_in' ? 'pos' : 'neg'}">${
            m.movement_type === 'paid_in' ? '+' : '−'
          }${escapeHtml(fc(m.amount))}</span>
          <span class="mv-reason">${escapeHtml(m.reason || '')}</span>
        </div>`,
        )
        .join('')}
    </div>`
      : ''

  const cash = `
    <div class="section">
      <div class="section-title">Cash drawer</div>
      <table class="kv">
        ${drawerLines.map((l) => kvRow(l.label, escapeHtml(fc(l.amount)))).join('')}
        ${kvRow('Expected in drawer', escapeHtml(fc(expected)), 'sum')}
        ${kvRow('Counted cash', escapeHtml(fc(counted)))}
        ${kvRow('Variance', escapeHtml(fc(variance)), Math.abs(variance) < 0.01 ? 'sum' : 'sum-flag')}
      </table>
      ${movementsList}
    </div>
  `

  const sparkline = `
    <div class="section">
      <div class="section-title">Hourly sales</div>
      ${printSparklineSvg(z.hourly, fc)}
    </div>
  `

  const shifts = renderShifts(z.shifts, fc)
  const voids = renderVoids(z.voids, z.void_total, fc)
  const refunds = renderRefunds(z.refunds, fc)
  const discounts = renderDiscounts(z.discounts, fc)
  const serviceCharges = renderServiceCharges(z.service_charge_by_staff, fc)
  const top = renderTop(z.top_categories, z.top_items, fc)

  const signoff = `
    <div class="section">
      <div class="section-title">Manager sign-off</div>
      ${z.day.closing_notes ? `<div class="notes">${escapeHtml(z.day.closing_notes)}</div>` : ''}
      <div class="signline">Signed: ____________________________</div>
    </div>
  `

  const footer = `
    <div class="footer">
      <div>${escapeHtml(seqLabel)} · locked ${escapeHtml(closeTime)} · ${escapeHtml(nameOrDash(z.day.closed_by_name) || '—')}</div>
      <div class="attr">software powered by bhookly.com</div>
    </div>
  `

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Z Report ${escapeHtml(seqLabel)}</title>
<style>${stylesheet(cfg)}</style>
</head><body>
<div class="paper">
  ${hdr}
  ${lifecycle}
  ${totals}
  ${tender}
  ${cash}
  ${shifts}
  ${sparkline}
  ${voids}
  ${refunds}
  ${discounts}
  ${serviceCharges}
  ${top}
  ${signoff}
  ${footer}
</div>
</body></html>`
}

function isReprintBanner(z: ZReportPayload): boolean {
  if (!z.day.closed_at || !z.generated_at) return false
  const closed = new Date(z.day.closed_at).getTime()
  const gen = new Date(z.generated_at).getTime()
  // If the sequence was assigned more than a minute after lock, this is
  // either the first print after a delay or a reprint; treat anything >2
  // minutes apart as a reprint for the on-slip indicator.
  return Math.abs(gen - closed) > 120_000
}

function kvRow(label: string, value: string, variant: 'plain' | 'sum' | 'sum-strong' | 'sum-flag' = 'plain'): string {
  const cls =
    variant === 'sum'
      ? 'row sum'
      : variant === 'sum-strong'
        ? 'row sum strong'
        : variant === 'sum-flag'
          ? 'row sum flag'
          : 'row'
  return `<tr class="${cls}"><td>${escapeHtml(label)}</td><td class="num">${value}</td></tr>`
}

/**
 * One tender line on the Z-slip.
 *
 * `counted == null` means nobody counted that tender — only reachable via
 * force-close now (F-CTRL-N33). It must print as "—", NOT as Rs 0.
 *
 * This used to do `counted ?? 0`, so an uncounted card tender printed on the
 * owner's end-of-day slip as "Counted 0, Δ −17,594" — a shortfall that never
 * happened, on the one document the owner actually reads. A number nobody
 * produced must never appear as though someone did.
 */
function tenderRow(
  label: string,
  system: number,
  counted: number | null | undefined,
  fc: (n: number) => string,
): string {
  if (counted == null) {
    return `<tr>
    <td>${escapeHtml(label)}</td>
    <td class="num">${escapeHtml(fc(system))}</td>
    <td class="num">—</td>
    <td class="num">—</td>
  </tr>`
  }
  const delta = counted - system
  const flag = Math.abs(delta) > 0.5 ? 'flag' : ''
  return `<tr>
    <td>${escapeHtml(label)}</td>
    <td class="num">${escapeHtml(fc(system))}</td>
    <td class="num">${escapeHtml(fc(counted))}</td>
    <td class="num ${flag}">${escapeHtml(fc(delta))}</td>
  </tr>`
}

// A tender nobody counts at close (sold today, collected later).
function uncountedTenderRow(label: string, amount: number, fc: (n: number) => string): string {
  return `<tr>
    <td>${escapeHtml(label)}</td>
    <td class="num">${escapeHtml(fc(amount))}</td>
    <td class="num"></td>
    <td class="num"></td>
  </tr>`
}

// subTenderRow — an indented online-provider line under the Online tender row
// (migration 050). System-amount only; Counted/Δ are blank because providers
// are not counted separately during Day Close.
function subTenderRow(label: string, amount: number, fc: (n: number) => string): string {
  return `<tr>
    <td style="padding-left:12px;opacity:0.75">↳ ${escapeHtml(label)}</td>
    <td class="num" style="opacity:0.75">${escapeHtml(fc(amount))}</td>
    <td class="num"></td>
    <td class="num"></td>
  </tr>`
}

// F-CTRL-N29: per-shift close roll-up. Prints one compact block per shift under
// the single daily Z. Omitted entirely for single-close tenants (empty shifts).
function renderShifts(shifts: ShiftClosing[] | undefined, fc: (n: number) => string): string {
  if (!shifts || shifts.length === 0) return ''
  const blocks = shifts
    .map((sh) => {
      const v = sh.cash_variance
      const vLabel = Math.abs(v) < 0.01 ? 'balanced' : v < 0 ? `${fc(Math.abs(v))} short` : `${fc(v)} over`
      const flag = Math.abs(v) < 0.01 ? '' : 'flag'
      return `
      <table class="kv">
        ${kvRow(`Shift ${sh.shift_number}`, `${escapeHtml(nameOrDash(sh.closed_by_name))} · ${escapeHtml(formatTimeHuman(sh.closed_at))}`, 'sum')}
        ${kvRow('Sales (cash/card/online)', `${escapeHtml(fc(sh.shift_cash_sales))} / ${escapeHtml(fc(sh.shift_card_sales))} / ${escapeHtml(fc(sh.shift_online_sales))}`)}
        ${kvRow('Orders', String(sh.shift_order_count))}
        ${kvRow('Drawer expected', escapeHtml(fc(sh.expected_cash)))}
        ${kvRow('Drawer counted', escapeHtml(fc(sh.counted_cash)))}
        ${kvRow('Variance', vLabel, flag ? 'sum-flag' : 'plain')}
        ${sh.cash_drop > 0 ? kvRow('Cash to safe', escapeHtml(fc(sh.cash_drop))) : ''}
      </table>`
    })
    .join('<div class="rule"></div>')
  return `
    <div class="section">
      <div class="section-title">Shift closes</div>
      ${blocks}
    </div>
  `
}

function renderVoids(voids: ZVoidEntry[], total: ZVoidsTotal, fc: (n: number) => string): string {
  if (!voids || voids.length === 0) {
    return `
      <div class="section">
        <div class="section-title">Voids</div>
        <div class="empty">No voids today</div>
      </div>
    `
  }
  const rows = voids
    .map(
      (v) => `
      <tr>
        <td>${escapeHtml(v.item_name)} ×${v.quantity}${v.outcome === 'wasted' ? ' (wasted)' : ''}</td>
        <td class="num">${escapeHtml(fc(v.line_value))}</td>
      </tr>
      ${v.reason ? `<tr class="sub"><td colspan="2">${escapeHtml(v.reason)}${v.voided_by ? ` — ${escapeHtml(v.voided_by)}` : ''}</td></tr>` : v.voided_by ? `<tr class="sub"><td colspan="2">${escapeHtml(v.voided_by)}</td></tr>` : ''}
    `,
    )
    .join('')
  return `
    <div class="section">
      <div class="section-title">Voids</div>
      <table class="tbl">${rows}</table>
      <div class="totalbar">
        <span>Total voids</span>
        <span class="num">${total.count} · ${escapeHtml(fc(total.value))}</span>
      </div>
      ${(total.wasted_count ?? 0) > 0 || (total.not_made_count ?? 0) > 0 ? `
      <table class="tbl">
        <tr class="sub"><td>Not made</td><td class="num">${total.not_made_count ?? 0} · ${escapeHtml(fc(total.not_made_value ?? 0))}</td></tr>
        <tr class="sub"><td>Made, thrown away</td><td class="num">${total.wasted_count ?? 0} · ${escapeHtml(fc(total.wasted_value ?? 0))}</td></tr>
        ${(total.waste_cost ?? 0) > 0 ? `<tr class="sub"><td>Food cost of what was thrown away</td><td class="num">${escapeHtml(fc(total.waste_cost ?? 0))}</td></tr>` : ''}
      </table>` : ''}
    </div>
  `
}

function renderRefunds(refunds: ZRefundEntry[], fc: (n: number) => string): string {
  if (!refunds || refunds.length === 0) {
    return `
      <div class="section">
        <div class="section-title">Refunds</div>
        <div class="empty">No refunds today</div>
      </div>
    `
  }
  const totalRefund = refunds.reduce((acc, r) => acc + r.amount, 0)
  const rows = refunds
    .map(
      (r) => `
      <tr>
        <td>${escapeHtml(r.order_number || r.order_id.slice(0, 8))} · ${escapeHtml(r.payment_method)}</td>
        <td class="num">${escapeHtml(fc(r.amount))}</td>
      </tr>
      ${r.processed_by ? `<tr class="sub"><td colspan="2">${escapeHtml(r.processed_by)} · ${escapeHtml(formatTimeHuman(r.processed_at))}</td></tr>` : ''}
    `,
    )
    .join('')
  return `
    <div class="section">
      <div class="section-title">Refunds</div>
      <table class="tbl">${rows}</table>
      <div class="totalbar">
        <span>Total refunds</span>
        <span class="num">${refunds.length} · ${escapeHtml(fc(totalRefund))}</span>
      </div>
    </div>
  `
}

function renderDiscounts(
  d: { total: number; order_count: number; avg_percent: number; card_promo_total?: number },
  fc: (n: number) => string,
): string {
  if (!d || d.order_count === 0) {
    return `
      <div class="section">
        <div class="section-title">Discounts</div>
        <div class="empty">No discounts today</div>
      </div>
    `
  }
  const avgPct = d.avg_percent > 0 ? `${d.avg_percent.toFixed(1)}%` : '—'
  // Card-promo share is bank-reimbursed — call it out so the owner reads
  // it as claimable, not margin given away.
  const cardPromoRow =
    (d.card_promo_total ?? 0) > 0
      ? kvRow('of which card promos (bank-paid)', escapeHtml(fc(d.card_promo_total ?? 0)))
      : ''
  return `
    <div class="section">
      <div class="section-title">Discounts</div>
      <table class="kv">
        ${kvRow('Total given', escapeHtml(fc(d.total)))}
        ${cardPromoRow}
        ${kvRow('Orders with discount', String(d.order_count))}
        ${kvRow('Average %', avgPct)}
      </table>
    </div>
  `
}

// F-RPT-N17: per-waiter service-charge split — the block the manager
// distributes staff shares from at close. Omitted entirely (like shift
// closes) when the tenant charges no service charge, so most slips are
// unchanged. Attribution is the order's assigned staff; orders without
// one pool into an "Unattributed" row.
function renderServiceCharges(
  rows: ZStaffServiceChargeRow[] | undefined,
  fc: (n: number) => string,
): string {
  if (!rows || rows.length === 0) return ''
  const total = rows.reduce((acc, r) => acc + r.amount, 0)
  const body = rows
    .map(
      (r) => `
      <tr>
        <td>${escapeHtml(r.name)} ×${r.order_count}</td>
        <td class="num">${escapeHtml(fc(r.amount))}</td>
      </tr>`,
    )
    .join('')
  return `
    <div class="section">
      <div class="section-title">Service charge by staff</div>
      <table class="tbl">${body}</table>
      <div class="totalbar">
        <span>Total service charge</span>
        <span class="num">${escapeHtml(fc(total))}</span>
      </div>
    </div>
  `
}

function renderTop(cats: ZCategoryRow[], items: ZItemRow[], fc: (n: number) => string): string {
  const catRows =
    cats && cats.length > 0
      ? cats
          .map(
            (c) => `<tr>
              <td>${escapeHtml(c.name)}</td>
              <td class="num">${c.quantity}</td>
              <td class="num">${escapeHtml(fc(c.net))}</td>
            </tr>`,
          )
          .join('')
      : `<tr><td colspan="3" class="empty">No category data</td></tr>`

  const itemRows =
    items && items.length > 0
      ? items
          .map(
            (i) => `<tr>
              <td>${escapeHtml(i.name)}</td>
              <td class="num">${i.quantity}</td>
              <td class="num">${escapeHtml(fc(i.net))}</td>
            </tr>`,
          )
          .join('')
      : `<tr><td colspan="3" class="empty">No item data</td></tr>`

  return `
    <div class="section">
      <div class="section-title">Top categories</div>
      <table class="tbl tbl-top">
        <thead><tr><th>Name</th><th class="num">Qty</th><th class="num">Net</th></tr></thead>
        <tbody>${catRows}</tbody>
      </table>
    </div>
    <div class="section">
      <div class="section-title">Top items</div>
      <table class="tbl tbl-top">
        <thead><tr><th>Name</th><th class="num">Qty</th><th class="num">Net</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table>
    </div>
  `
}

function printSparklineSvg(buckets: ZHourBucket[], fc: (n: number) => string): string {
  const bars: ZHourBucket[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    gross: 0,
    order_count: 0,
  }))
  let max = 0
  let peak: ZHourBucket | null = null
  let total = 0
  for (const b of buckets || []) {
    if (b.hour < 0 || b.hour > 23) continue
    bars[b.hour] = b
    total += b.gross
    if (b.gross > max) {
      max = b.gross
      peak = b
    }
  }

  if (total <= 0) {
    return `<div class="empty">No hourly data</div>`
  }

  const w = 260
  const h = 56
  const padX = 4
  const labelGap = 10
  const innerH = h - labelGap
  const slot = (w - padX * 2) / 24
  const bw = Math.max(2, slot * 0.7)

  const rects = bars
    .map((b, i) => {
      const bh = b.gross > 0 ? Math.max(1, (b.gross / (max || 1)) * innerH) : 0
      const x = padX + i * slot + (slot - bw) / 2
      const y = innerH - bh
      const isPeak = peak?.hour === b.hour
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${bh.toFixed(1)}" fill="${isPeak ? '#000' : '#222'}" />`
    })
    .join('')

  const ticks = [0, 6, 12, 18]
    .map(
      (hour) =>
        `<text x="${(padX + hour * slot + slot / 2).toFixed(1)}" y="${h - 1}" text-anchor="middle" font-size="6" fill="#444">${formatHourTick(hour)}</text>`,
    )
    .join('')

  const peakLabel = peak
    ? `<div class="peak">Peak ${formatHourTick(peak.hour)} · ${escapeHtml(fc(peak.gross))}</div>`
    : ''

  return `
    <svg viewBox="0 0 ${w} ${h}" width="100%" height="48" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style="display:block">
      ${rects}
      ${ticks}
    </svg>
    ${peakLabel}
  `
}

function formatHourTick(h: number): string {
  if (h === 0) return '12a'
  if (h === 12) return '12p'
  if (h < 12) return `${h}a`
  return `${h - 12}p`
}

function nameOrDash(v: string | null | undefined): string {
  if (!v) return '—'
  return escapeHtml(v)
}

function stylesheet(cfg: CustomerReceiptSettings): string {
  const baseCss = thermalPrintBaseCss({
    paperWidthMm: cfg.paperWidthMm,
    printableAreaMm: cfg.printableAreaMm,
    fontScale: cfg.fontScale,
  })
  const w = cfg.printableAreaMm
  const scale = cfg.fontScale
  return `
    ${baseCss}
    body {
      font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
      font-size: calc(11px * ${scale});
      font-weight: 700;
      line-height: 1.35;
    }
    .paper { width: ${w}mm; max-width: ${w}mm; margin: 0 auto; padding: 2mm 2mm 5mm; box-sizing: border-box; }
    .hdr { text-align: center; }
    .logo { max-width: 70%; max-height: 28mm; object-fit: contain; margin: 0 auto 2mm; display: block; }
    .biz { font-size: calc(16px * ${scale}); font-weight: 900; letter-spacing: 0.02em; margin-bottom: 1mm; }
    .meta { font-size: calc(10px * ${scale}); color: #000; }
    .title { text-align: center; font-weight: 900; font-size: calc(13px * ${scale}); margin: 3mm 0 1mm; letter-spacing: 0.05em; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 1mm 0; }
    .zhead { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5mm 3mm; margin: 1mm 0 0; font-size: calc(10px * ${scale}); }
    .zhead .lbl { color: #444; margin-right: 1mm; }
    .zhead .val { font-weight: 800; }
    .reprint { text-align: center; font-weight: 900; letter-spacing: 0.2em; font-size: calc(11px * ${scale}); margin: 1mm 0; border: 1px dashed #000; padding: 0.5mm; }
    .section { margin-top: 3mm; }
    .section-title { font-weight: 900; font-size: calc(11px * ${scale}); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #000; padding-bottom: 0.5mm; margin-bottom: 1mm; }
    table { width: 100%; border-collapse: collapse; }
    table.kv td { padding: 0.4mm 0; vertical-align: top; font-size: calc(11px * ${scale}); }
    table.kv td:last-child { text-align: right; font-variant-numeric: tabular-nums; padding-right: 1mm; }
    .row.sum td { border-top: 1px dashed #000; padding-top: 0.8mm; font-weight: 800; }
    .row.sum.strong td { border-top: 1px solid #000; border-bottom: 1px solid #000; font-weight: 900; padding: 0.8mm 0; }
    .row.sum.flag td:last-child { font-weight: 900; text-decoration: underline; }
    .rule { border-top: 1px dashed #000; margin: 1mm 0; }
    table.tender { font-size: calc(10.5px * ${scale}); }
    table.tender th { text-align: left; font-weight: 800; padding: 0.4mm 0; border-bottom: 1px dashed #000; }
    table.tender th.num, table.tender td.num { text-align: right; font-variant-numeric: tabular-nums; padding-right: 1mm; }
    table.tender td { padding: 0.4mm 0; }
    table.tender td.flag { font-weight: 900; text-decoration: underline; }
    .movements { margin-top: 1.5mm; padding-top: 1mm; border-top: 1px dashed #000; }
    .mv { display: flex; justify-content: space-between; gap: 2mm; font-size: calc(10.5px * ${scale}); padding: 0.2mm 0; }
    .mv-sign { font-variant-numeric: tabular-nums; font-weight: 800; min-width: 18mm; text-align: right; padding-right: 1mm; }
    .mv-sign.pos { color: #000; }
    .mv-sign.neg { color: #000; }
    .mv-reason { flex: 1; }
    table.tbl { font-size: calc(10.5px * ${scale}); }
    table.tbl td { padding: 0.3mm 0; vertical-align: top; }
    table.tbl td.num { text-align: right; font-variant-numeric: tabular-nums; padding-right: 1mm; }
    table.tbl tr.sub td { padding-left: 2mm; font-size: calc(9.5px * ${scale}); color: #333; font-style: italic; }
    table.tbl-top th { font-size: calc(9.5px * ${scale}); font-weight: 800; text-transform: uppercase; padding: 0.3mm 0; border-bottom: 1px dashed #000; }
    table.tbl-top th.num { text-align: right; padding-right: 1mm; }
    .totalbar { display: flex; justify-content: space-between; margin-top: 1mm; padding-top: 1mm; border-top: 1px dashed #000; font-weight: 800; font-size: calc(10.5px * ${scale}); }
    .totalbar .num { font-variant-numeric: tabular-nums; padding-right: 1mm; }
    .empty { text-align: center; font-size: calc(10px * ${scale}); color: #444; padding: 1mm 0; }
    .peak { font-size: calc(10px * ${scale}); text-align: right; margin-top: 0.5mm; font-weight: 800; }
    .notes { font-style: italic; font-size: calc(10.5px * ${scale}); padding: 1mm 0; border-left: 2px solid #000; padding-left: 2mm; margin-bottom: 2mm; }
    .signline { margin-top: 4mm; font-size: calc(11px * ${scale}); }
    .footer { margin-top: 4mm; padding-top: 1mm; border-top: 1px dashed #000; text-align: center; font-size: calc(9.5px * ${scale}); color: #333; }
    .footer .attr { margin-top: 1mm; font-style: italic; }
  `
}
